// Date formats for Moment JS.
//
// @see https://momentjs.com/docs/#/i18n/instance-locale/
import Moment from 'moment'
import FormatNumber from '@/filters/number'
import Translate from '@/filters/translate'

// formatNumber is a helper function used only for posters. The reason why we use a dedicated function in this file
// only, rather than using the regular FormatNumber filter is partially because we want to use the poster locale - not
// the site language - as the foundation for the language's separators, and partially because we have many more poster
// locales than we have site languages.
//
// This function has been based on the following references.
//
// @see https://en.wikipedia.org/wiki/Decimal_separator#Digit_grouping
// @see https://docs.oracle.com/cd/E19455-01/806-0169/overview-9/index.html
const formatNumber = function (number, locale) {
  let thousandSeparator = ' '
  let decimalSeparator = ','

  switch (locale) {
    // Uses default, provided for reference.
    case 'da':
    case 'fi':
    case 'de':
    case 'et':
    case 'fr':
    case 'fo':
    case 'is':
    case 'hu':
    case 'nl':
    case 'nb':
    case 'pl':
    case 'pt':
      break
    case 'es':
    case 'it':
      thousandSeparator = '.'
      decimalSeparator = ','
      break
    case 'en-gb':
    case 'en-us':
      thousandSeparator = ','
      decimalSeparator = '.'
      break
  }

  return FormatNumber.formatRaw(number, 'auto', thousandSeparator, decimalSeparator)
}

// metaData holds information about the posters themselves that aren't part of generating them, such as the artist,
// dimensions, colors etc.
//
// Note that the colors listed here are for use in the interface. It is up to the developers to add the color
// implementation to the poster component in question if a color is added here.
//
// The 'name' property will be set on the poster SVG as a class, and the 'color' property will be set as the background
// in the poster's color selector.
//
// Note that the template set here is only partial - the back end has a template name in the format
// "[name]-[color]-[presentation]", which must be compiled in the front end when setting the SVG.
//
// Examples: "babyroo-blue-full-width", or "retro-baby-pink-white-frame".
//
// The product IDs are also built from variables here, and they follow the below pattern:
// "[baseID]-[colorID]-[presentationID]", where presentationID is 01 for full-size and 02 for white frame. That
// Example (Foxy Strollin', mustard, white frame): "IPA-0003-02-02".
//
// Worth noting is that the letters at the start (IPA in the above example) represents the artist, so IPA for Ingela P.
// Arrhenius. It is included in the base ID though, so it doesn't have to be set manually when building the IDs.
//
// The IDs are built in the structured data getters.
//
// @todo Rename to "posters" and allow retrieving them all.
const metaData = {
  // Ingela P. Arrhenius.
  'shorty-and-stretch': {
    key: 'shorty-and-stretch',
    template: 'shorty-and-stretch',
    baseID: 'IPA-0010',
    artist: 'Ingela P Arrhenius',
    name: 'Shorty and Stretch',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f7eaca', ID: '01' },
      { name: 'green', color: '#4ca194', ID: '02' }
    ]
  },
  'peekaboo': {
    key: 'peekaboo',
    template: 'peekaboo',
    baseID: 'IPA-0001',
    artist: 'Ingela P Arrhenius',
    name: 'Peekaboo',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f4f0e0', ID: '01' },
      { name: 'blue', color: '#55a6c5', ID: '02' }
    ]
  },
  'retro-baby': {
    key: 'retro-baby',
    template: 'retro-baby',
    baseID: 'IPA-0006',
    artist: 'Ingela P Arrhenius',
    name: 'Retro Baby',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'pink', color: '#d37fa7', ID: '02' },
      { name: 'red', color: '#cc3e3b', ID: '01' }
    ]
  },
  'owlin-for-you': {
    key: 'owlin-for-you',
    template: 'owlin-for-you',
    baseID: 'IPA-0005',
    artist: 'Ingela P Arrhenius',
    name: 'Owlin\' for You',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f4eacb', ID: '01' },
      { name: 'mustard', color: '#f2cc6e', ID: '02' }
    ]
  },
  'born-strong': {
    key: 'born-strong',
    template: 'born-strong',
    baseID: 'IPA-0008',
    artist: 'Ingela P Arrhenius',
    name: 'Born Strong',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f4eacb', ID: '01' },
      { name: 'black', color: '#010110', ID: '02' }
    ]
  },
  'what-the-fox-say': {
    key: 'what-the-fox-say',
    template: 'what-the-fox-say',
    baseID: 'IPA-0009',
    artist: 'Ingela P Arrhenius',
    name: 'What The Fox Say',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f4ecde', ID: '01' },
      { name: 'green', color: '#2c685b', ID: '02' }
    ]
  },
  'scandinavian-forest': {
    key: 'scandinavian-forest',
    template: 'scandinavian-forest',
    baseID: 'IPA-0007',
    artist: 'Ingela P Arrhenius',
    name: 'Scandinavian Forest',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f4ecde', ID: '01' },
      { name: 'green', color: '#4e9c86', ID: '02' }
    ]
  },
  'foxy-strollin': {
    key: 'foxy-strollin',
    template: 'foxy-strollin',
    baseID: 'IPA-0003',
    artist: 'Ingela P Arrhenius',
    name: 'Foxy Strollin\'',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f4ecde', ID: '01' },
      { name: 'mustard', color: '#e2ad53', ID: '02' }
    ]
  },
  'mockingbird': {
    key: 'mockingbird',
    template: 'mockingbird',
    baseID: 'IPA-0004',
    artist: 'Ingela P Arrhenius',
    name: 'Mockingbird',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'beige', color: '#f4ecde', ID: '01' },
      { name: 'green', color: '#4d9d99', ID: '02' }
    ]
  },
  'babyroo': {
    key: 'babyroo',
    template: 'babyroo',
    baseID: 'IPA-0002',
    artist: 'Ingela P Arrhenius',
    name: 'Baby roo',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'blue', color: '#477d9c', ID: '01' },
      { name: 'red', color: '#e06a42', ID: '02' }
    ]
  },

  // Katarzyna Stróżyńska Goraj
  'blossom-a': {
    key: 'blossom-a',
    template: 'blossom-letter',
    baseID: 'KSG-0001',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: A',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-b': {
    key: 'blossom-b',
    template: 'blossom-letter',
    baseID: 'KSG-0002',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: B',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-c': {
    key: 'blossom-c',
    template: 'blossom-letter',
    baseID: 'KSG-0003',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: C',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-d': {
    key: 'blossom-d',
    template: 'blossom-letter',
    baseID: 'KSG-0004',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: D',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-e': {
    key: 'blossom-e',
    template: 'blossom-letter',
    baseID: 'KSG-0005',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: E',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-f': {
    key: 'blossom-f',
    template: 'blossom-letter',
    baseID: 'KSG-0006',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: F',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-g': {
    key: 'blossom-g',
    template: 'blossom-letter',
    baseID: 'KSG-0007',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: G',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-h': {
    key: 'blossom-h',
    template: 'blossom-letter',
    baseID: 'KSG-0008',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: H',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-i': {
    key: 'blossom-i',
    template: 'blossom-letter',
    baseID: 'KSG-0009',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: I',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-j': {
    key: 'blossom-j',
    template: 'blossom-letter',
    baseID: 'KSG-0010',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: J',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-k': {
    key: 'blossom-k',
    template: 'blossom-letter',
    baseID: 'KSG-0011',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: K',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-l': {
    key: 'blossom-l',
    template: 'blossom-letter',
    baseID: 'KSG-0012',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: L',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-m': {
    key: 'blossom-m',
    template: 'blossom-letter',
    baseID: 'KSG-0013',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: M',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-n': {
    key: 'blossom-n',
    template: 'blossom-letter',
    baseID: 'KSG-0014',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: N',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-o': {
    key: 'blossom-o',
    template: 'blossom-letter',
    baseID: 'KSG-0015',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: O ',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-p': {
    key: 'blossom-p',
    template: 'blossom-letter',
    baseID: 'KSG-0016',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'BLossom: P',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-q': {
    key: 'blossom-q',
    template: 'blossom-letter',
    baseID: 'KSG-0017',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Q',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-r': {
    key: 'blossom-r',
    template: 'blossom-letter',
    baseID: 'KSG-0018',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: R',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-s': {
    key: 'blossom-s',
    template: 'blossom-letter',
    baseID: 'KSG-0019',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: S',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-t': {
    key: 'blossom-t',
    template: 'blossom-letter',
    baseID: 'KSG-0020',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: T',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-u': {
    key: 'blossom-u',
    template: 'blossom-letter',
    baseID: 'KSG-0021',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: U',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-v': {
    key: 'blossom-v',
    template: 'blossom-letter',
    baseID: 'KSG-0022',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: V',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-w': {
    key: 'blossom-w',
    template: 'blossom-letter',
    baseID: 'KSG-0023',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: W',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-x': {
    key: 'blossom-x',
    template: 'blossom-letter',
    baseID: 'KSG-0024',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: X',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-y': {
    key: 'blossom-y',
    template: 'blossom-letter',
    baseID: 'KSG-0025',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Y',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-z': {
    key: 'blossom-z',
    template: 'blossom-letter',
    baseID: 'KSG-0026',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Z',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-aring': {
    key: 'blossom-aring',
    template: 'blossom-letter',
    baseID: 'KSG-0027',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Å',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-auml': {
    key: 'blossom-auml',
    template: 'blossom-letter',
    baseID: 'KSG-0028',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Ä',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-ouml': {
    key: 'blossom-ouml',
    template: 'blossom-letter',
    baseID: 'KSG-0029',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Ö',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-aelig': {
    key: 'blossom-aelig',
    template: 'blossom-letter',
    baseID: 'KSG-0030',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Æ',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-oslash': {
    key: 'blossom-oslash',
    template: 'blossom-letter',
    baseID: 'KSG-0031',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Ø',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'blossom-yuml': {
    key: 'blossom-yuml',
    template: 'blossom-letter',
    baseID: 'KSG-0032',
    artist: 'Katarzyna Stróżyńska Goraj',
    name: 'Blossom: Ÿ',
    dimensions: '50x70',
    prices: {
      SEK: 60000,
      DKK: 45000,
      EUR: 6000,
      USD: 7000,
      AUD: 9500
    },
    colors: [
      { name: 'grey', color: '#edeceb', ID: '01' },
      { name: 'beige', color: '#f4f0e8', ID: '02' },
      { name: 'slate-grey', color: '#2c2d34', ID: '03' },
      { name: 'burgundy', color: '#2b1619', ID: '04' }
    ]
  },
  'default': {
    key: '',
    template: '',
    artist: 'artistNotSpecified',
    name: 'nameNotSpecified',
    dimensions: '50x70',
    prices: {
      SEK: 0,
      DKK: 0,
      EUR: 0,
      USD: 0
    },
    colors: []
  }
}

// @see https://momentjs.com/ (click a language and see the example two-letter code output)
/* eslint-disable no-multi-spaces */
const localeMap = [
  { name: 'Dansk', value: 'da' },           // Danish
  { name: 'Deutsch', value: 'de' },         // German
  { name: 'Eesti', value: 'et' },           // Estonian
  { name: 'English (UK)', value: 'en-gb' }, // English (UK)
  { name: 'English (US)', value: 'en-us' }, // English (US)
  { name: 'Español', value: 'es' },         // Spanish (@todo Catalan?)
  { name: 'Français', value: 'fr' },        // French
  { name: 'Føroyskt mál', value: 'fo' },    // Faroese
  { name: 'Íslenska', value: 'is' },        // Icelandic
  { name: 'Italiano', value: 'it' },        // Italian
  { name: 'Magyar', value: 'hu' },          // Hungarian
  { name: 'Nederlands', value: 'nl' },      // Dutch
  { name: 'Norsk', value: 'nb' },           // Norwegian (Bokmål)
  { name: 'Polski', value: 'pl' },          // Polish
  { name: 'Português', value: 'pt' },       // Portuguese
  { name: 'Suomi', value: 'fi' },           // Finnish
  { name: 'Svenska', value: 'sv' }          // Swedish
  // {name: 'Pусский', value: 'ru'},         // Russian
  // {name: '日本語', value: 'ja'}            // Japanese
]

const presentationMap = [
  'fullsize',
  'whiteFrame'
]

// presentationSVGValues holds the different width, height, X and Y values that are used in the SVG to have the inner
// content be the correct size. They assume that the PDFs are 2266 in width and 3132 in height, as per the Baby roo
// poster that was obtained during development.
//
// The values will be used to create the proper white border depending on the poster's presentation, making sure that
// the printing information will always be correct.
//
// The values should be placed on an SVG just inside the primary SVG container with the printing references crosses (in
// the corner) and the white background as siblings.
const presentationSVGValues = {
  fullsize: {
    width: 2342,
    height: 3254,
    x: -39,
    y: -46
    /*
    width: 2342,
    height: 3254,
    x: -39,
    y: -46
    */
  },
  whiteFrame: {
    width: 1904,
    height: 2770,
    x: 181,
    y: 181
  }
}

export default {
  strict: true,

  state: {
    // The selector for the poster we want to work with. It will define which values are returned by some getters, such as
    // the artist and the poster's human readable name.
    posterSelector: 'babyroo',

    // The child's first name.
    childName: 'Frances',

    // The timestamp of birth. Note that it is a string for reasons related to committing to local storage. We will
    // parse the object when it's read.
    birthDate: '',

    // Meridiem holds the AM / PM value of the poster. Empty string means 24 hour format.
    meridiem: '',

    // The unit to present values in. Accepts 'metric' and 'imperial'.
    unit: 'metric',

    // The child's weight. Note that either "grams" or both lbs and oz will be used, depending on the unit.
    weight: { grams: 3500, lbs: 7, oz: 11 },

    // The child's length in, will be displayed as "cm" (metric) or "in" (imperial) depending on the unit.
    length: 50,

    // locale tells the poster which language we're working with.
    // Can't use the store to read a default value here as it's a race condition. It's instead lazily set on first read.
    locale: null,

    // presentation tells us how the image should be fitted within the printed poster.
    presentation: 'fullsize',

    // color is the color that has been selected for the poster. If a new poster is selected, this value is replaced by
    // its default (first) color.
    colorIndex: 0
  },

  mutations: {
    SET_POSTER (state, selector) {
      state.posterSelector = selector
    },

    SET_CHILD_NAME (state, name) {
      state.childName = name
    },

    SET_CHILD_BIRTH_DATE (state, date) {
      state.birthDate = date
    },

    SET_MERIDIEM (state, meridiem) {
      state.meridiem = meridiem
    },

    SET_UNIT (state, unit) {
      state.unit = unit
    },

    SET_WEIGHT (state, weight) {
      state.weight = weight
    },

    SET_LENGTH (state, length) {
      state.length = length
    },

    SET_LOCALE (state, locale) {
      state.locale = locale
    },

    SET_PRESENTATION (state, presentation) {
      state.presentation = presentation
    },

    SET_COLOR (state, color) {
      state.colorIndex = color
    }
  },

  actions: {
    setPoster ({ commit }, selector) {
      // Make sure the poster exists.
      if (!metaData[selector]) {
        console.log('can\'t set unknown poster \'' + selector + '\'')
        return
      }
      commit('SET_POSTER', selector)

      // Reset the poster color.
      let m = metaData[selector].colors
      if (!m) {
        return
      }
      commit('SET_COLOR', Object.entries(m)[0][0])
    },

    setPosterChildName ({ commit }, name) {
      commit('SET_CHILD_NAME', name)
    },

    setPosterBirthDate ({ commit }, date) {
      if (!Moment.isMoment(date)) {
        return
      }
      commit('SET_CHILD_BIRTH_DATE', date.format())
    },

    setPosterMeridiem ({ state, commit, getters }, meridiem) {
      let bd = getters.posterBirthDate

      // Validate
      switch (meridiem) {
        case '':
        case 'am':
        case 'pm':
          break
      }

      // Update time value.
      switch (true) {
        case meridiem === 'am' && bd.hours() > 12:
          bd.set('hours', bd.hours() - 12)
          break
        case meridiem === 'pm' && bd.hours() < 12:
          bd.set('hours', bd.hours() + 12)
          break
      }

      commit('SET_MERIDIEM', meridiem)
      commit('SET_CHILD_BIRTH_DATE', bd)
    },

    setPosterUnit ({ commit, getters }, unit) {
      let newLength
      switch (unit) {
        case 'metric':
          if (getters.posterUnit !== 'metric') {
            newLength = getters.posterLength * 2.5
            commit('SET_LENGTH', newLength)
          }
          commit('SET_UNIT', unit)
          break
        case 'imperial':
          if (getters.posterUnit !== 'imperial') {
            newLength = getters.posterLength / 2.5
            commit('SET_LENGTH', newLength)
          }
          commit('SET_UNIT', unit)
          break
      }
    },

    setPosterWeight ({ commit }, weight) {
      if (isNaN(weight.grams) || isNaN(weight.lbs) || isNaN(weight.oz)) {
        return
      }
      console.log(weight)
      commit('SET_WEIGHT', weight)
    },

    setPosterLength ({ commit }, length) {
      let valueToSet = parseFloat(length)
      switch (true) {
        // Avoid failing the component validation if the length is parsable as an int.
        case isNaN(valueToSet):
          valueToSet = 0
          break
        // No value in the model yields a non-numeric value.
        case this.birthLength === '':
          valueToSet = 0
          break
        // Disallow negative weight.
        case this.birthLength < 0:
          valueToSet = 0
          break
      }
      commit('SET_LENGTH', valueToSet)
    },

    setPosterLocale ({ commit }, locale) {
      let localeIsAllowed = false
      Object.entries(localeMap).forEach(function (item) {
        if (item[1].value === locale) {
          localeIsAllowed = true
        }
      })

      if (localeIsAllowed) {
        commit('SET_LOCALE', locale)
      }
    },

    setPosterPresentation ({ commit }, presentation) {
      if (presentationMap.indexOf(presentation) < 0) {
        return
      }
      commit('SET_PRESENTATION', presentation)
    },

    setPosterColor ({ state, commit }, index) {
      let availableColors = metaData[state.posterSelector].colors
      if (!availableColors || availableColors.length < 1) {
        return
      }
      commit('SET_COLOR', availableColors[index] ? index : 0)
    }
  },

  getters: {

    posterIsMutated (state) {
      let ret = 0;
      if (state.childName !== 'Frances') {
        ret += 1;
      }
      if (state.birthDate !== '') {
        ret += 1;
      }
      if (state.weight.grams !== 3500) {
        ret += 1;
      }
      if (state.weight.lbs !== 7) {
        ret += 1;
      }
      if (state.weight.oz !== 11) {
        ret += 1;
      }
      if (state.length !== 50) {
        ret += 1;
      }
      return ret > 2;
    },

    posterName (state) {
      return metaData[state.posterSelector].name
    },

    posterArtist (state) {
      return metaData[state.posterSelector].artist
    },

    posterChildName (state) {
      return state.childName
    },

    posterBirthDate (state, getters) {
      let ret = state.birthDate
      switch (true) {
        case state.birthDate === '':
          ret = Moment()
          break
        case typeof state.birthDate === 'string':
          ret = Moment(state.birthDate)
          break
      }
      // Need to reset the date. Otherwise, the invalid value is stored to
      // the local storage and it is impossible to fix it.
      if (ret.isValid() === false) {
        ret = Moment()
      }
      ret.locale(getters.posterLocale)
      return ret
    },

    posterMeridiem (state) {
      return state.meridiem
    },

    posterBirthTimeFormatted (state, getters) {
      switch (getters.posterMeridiem) {
        case 'am':
        case 'pm':
          return getters.posterBirthDate.format('hh:mm a')
      }

      // Default, 24 hours.
      return getters.posterBirthDate.format('HH:mm')
    },

    posterUnit (state) {
      return state.unit
    },

    posterWeight (state) {
      return state.weight
    },

    // @todo Language should implement proper formatting.
    posterWeightFormatted (state, getters) {
      switch (state.unit) {
        case 'metric':
          return formatNumber(state.weight.grams, getters.posterLocale) + ' g'
        case 'imperial':
          return formatNumber(state.weight.lbs, getters.posterLocale) + ' lbs ' + formatNumber(state.weight.oz, getters.posterLocale) + ' oz'
      }
      return 0
    },

    posterLength (state) {
      return state.length
    },

    posterLengthFormatted (state, getters) {
      let val = formatNumber(state.length, getters.posterLocale)
      return state.unit === 'imperial' ? val + ' in' : val + ' cm'
    },

    posterLocale (state, getters) {
      let posterLocale = state.locale
      if (posterLocale === null) {
        posterLocale = getters.getCurrLanguage.iso === 'en' ? 'en-gb' : getters.getCurrLanguage.iso
      }

      return posterLocale
    },

    posterLocales (state, getters) {
      let ret = JSON.parse(JSON.stringify(localeMap))

      // Set a language to default to.
      let localeToMatch = state.locale
      if (localeToMatch === null) {
        localeToMatch = getters.getCurrLanguage.iso === 'en' ? 'en-gb' : getters.getCurrLanguage.iso
      }

      ret.forEach(function (item) {
        if (item.value === localeToMatch) {
          item.preSelected = true
        }
      })

      return ret
    },

    posterPresentation (state) {
      return state.presentation
    },

    posterPresentationID (state) {
      switch (state.presentation) {
        case 'fullsize':
          return '01'
        case 'whiteFrame':
          return '02'
      }
      return ''
    },

    posterPresentations (state) {
      let ret = []
      presentationMap.forEach(function (p) {
        ret.push({
          name: Translate.translate(p),
          value: p,
          preSelected: state.presentation === p
        })
      })
      return JSON.parse(JSON.stringify(ret))
    },

    posterPresentationSVGValues (state) {
      let ret = presentationSVGValues[state.presentation]
      if (!ret) {
        return presentationSVGValues.fullsize
      }
      return ret
    },

    posterColors (state) {
      let ret = metaData[state.posterSelector].colors
      if (ret) {
        return ret
      }
      return []
    },

    // @todo The color is currently dissociated from the poster meta data, which means it must be reset every time we switch poster. Fix.
    posterColor (state) {
      let availableColors = metaData[state.posterSelector].colors
      if (!availableColors) {
        return []
      }

      // If we've used a poster with another color set.
      return availableColors[state.colorIndex] ? availableColors[state.colorIndex] : availableColors[0]
    },

    postersMetaData (state) {
      return JSON.parse(JSON.stringify(metaData))
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/poster.js